import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * `<app-eea-icon>` — European Economic Area territorial signal.
 *
 * A 12-star EU-flag ring, no letters, no padlock, no outer text. The
 * whole mark tints via `currentColor` so callers can inline it in a
 * paragraph, a chip, or a data cell and it picks up the surrounding
 * text colour with zero extra CSS.
 *
 * Semantic: signals "European Economic Area" — used to flag EEA-only
 * data flows and cross-border transfers on ROPA / DPIA surfaces. NOT
 * the GDPR framework mark; see `<app-framework-icon type="GDPR">` for
 * that (padlock + EU-star ring).
 *
 * Sizes:
 *   `sm`  → 14 px — inline text (chip / stat line)
 *   `md`  → 16 px — default, meta-icon row
 *   `lg`  → 24 px — hero position (compliance banner / gap card)
 *
 * All three variants render the same underlying SVG (24×24 viewBox)
 * scaled via a host class; the 5-point-star geometry holds at 20 px+
 * and degrades gracefully to a dotted EU ring at 14–16 px (still
 * recognisable EU imagery).
 *
 * Accessibility: `role="img"` + a fixed aria-label announces the
 * territorial semantic to screen readers. Callers can override via
 * the `label` input if the surrounding context needs a phrasing like
 * "EEA only" or "Outside EEA".
 */
@Component({
  selector: 'app-eea-icon',
  templateUrl: './eea-icon.html',
  styleUrl: './eea-icon.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': "'eea eea-' + size()",
    role: 'img',
    '[attr.aria-label]': 'label()',
  },
})
export class EeaIconComponent {
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  readonly label = input<string>('European Economic Area');
}
